import { format } from "date-fns"
import type { DateTimeString } from "./model"

/**
 * Timestamp in milliseconds or ISO date string or date object
 */
type DateInput = number | DateTimeString | Date

const createDate = (date: DateInput) =>
  typeof date === "object" ? date : new Date(date)

const formatters = {
  long: new Intl.DateTimeFormat(undefined, {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    second: "2-digit",
  }),
  short: new Intl.DateTimeFormat(undefined, {
    year: "numeric",
    month: "numeric",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    second: "2-digit",
  }),
  date: new Intl.DateTimeFormat(undefined, {
    year: "numeric",
    month: "numeric",
    day: "numeric",
  }),
}

export const formatDate = (format: keyof typeof formatters, date: DateInput) =>
  formatters[format].format(createDate(date))

export const getDateStringComparator =
  (order: "asc" | "desc") => (a: DateInput, b: DateInput) =>
    (order === "asc" ? 1 : -1) *
    (createDate(a).getTime() - createDate(b).getTime())

export const isAfter = (date: DateInput, compareTo: DateInput) =>
  createDate(date).getTime() > createDate(compareTo).getTime()

/** Undefined for anything a date input cannot show: absent, malformed, or out of range. */
export const parseDate = (value: string | undefined): Date | undefined => {
  if (!value) {
    return undefined
  }

  const date = new Date(value)

  return Number.isNaN(date.getTime()) ? undefined : date
}

export const toLocalDatetime = (date: Date | undefined): string => {
  if (!date) {
    return ""
  }

  return format(date, "yyyy-MM-dd'T'HH:mm")
}
